// DraggableTable.jsx
import React, { createContext, useContext, useState, useCallback } from 'react';
import { Table } from 'antd';
import {
  DndContext,
  DragOverlay,
  PointerSensor,
  useSensor,
  useSensors,
  closestCenter,
} from '@dnd-kit/core';
import { arrayMove, SortableContext, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';

const DragIndexContext = createContext({
  active: -1,
  over: -1,
});

const dragActiveStyle = (dragState, id) => {
  const { active, over } = dragState;
  let style = {};
  if (active && active === id) {
    style = {
      backgroundColor: 'gray',
      opacity: 0.5,
    };
  } else if (over && id === over && active !== over) {
    style = {
      border: '1px dashed gray',
    };
  }
  return style;
};

const TableBodyRow = ({ children, ...props }) => {
  const dragState = useContext(DragIndexContext);
  const { attributes, listeners, setNodeRef, isDragging } = useSortable({
    id: props['data-row-key'],
  });
  const style = {
    ...props.style,
    cursor: 'move',
    ...(isDragging ? {
      position: 'relative',
      zIndex: 9999,
      userSelect: 'none',
    } : {}),
    ...dragActiveStyle(dragState, props['data-row-key']),
  };
  return (
    <tr {...props} ref={setNodeRef} style={style} {...attributes} {...listeners}>
      {children}
    </tr>
  );
};

const DraggableTable = ({ columns, data, setData, tableProps }) => {
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 5,
      },
    })
  );

  const [dragIndex, setDragIndex] = useState({ active: -1, over: -1 });

  const onDragEnd = ({ active, over }) => {
    if (active.id !== over?.id) {
      setData((prevState) => {
        const activeIndex = prevState.findIndex((i) => i.key === active?.id);
        const overIndex = prevState.findIndex((i) => i.key === over?.id);
        return arrayMove(prevState, activeIndex, overIndex);
      });
    }
    setDragIndex({ active: -1, over: -1 });
  };

  const onDragOver = ({ active, over }) => {
    const activeIndex = data.findIndex((i) => i.key === active.id);
    const overIndex = data.findIndex((i) => i.key === over?.id);
    setDragIndex({
      active: active.id,
      over: over?.id,
      direction: overIndex > activeIndex ? 'down' : 'up',
    });
  };

  return (
    <DndContext
      sensors={sensors}
      onDragEnd={onDragEnd}
      onDragOver={onDragOver}
      collisionDetection={closestCenter}
    >
      <SortableContext items={data.map((i) => i.key)} strategy={verticalListSortingStrategy}>
        <DragIndexContext.Provider value={dragIndex}>
          <Table
            columns={columns}
            dataSource={data}
            rowKey="key"
            components={{
              body: {
                row: TableBodyRow,
              },
            }}
            {...tableProps}
          />
        </DragIndexContext.Provider>
      </SortableContext>
      <DragOverlay>
        <tr
          style={{
            backgroundColor: 'gray',
            padding: 16,
          }}
        >
          {data.find((i) => i.key === dragIndex.active)?.symbol}
        </tr>
      </DragOverlay>
    </DndContext>
  );
};

export default DraggableTable;